<script setup>
import { ref, watch, onMounted, reactive, computed } from 'vue';
import { useToast } from "primevue/usetoast";
import { useMainStore } from '@/stores/store.js';
import { useRouter } from 'vue-router';
import { callAPI, DRUPAL_API_ROUTE } from "@/service/DrupalService";
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { useI18n } from 'vue-i18n'

const { t } = useI18n();

const schema = yup.object({
  title: yup.string().required().label('Title'),
  description: yup.string().required().label('Description'),
  facebook: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^https?:\/\/(www\.)?facebook\.com\/.+$/i,
      t('page.errors.facebook')
    ).label('Facebook'),
  instagram: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^https?:\/\/(www\.)?instagram\.com\/.+$/i,
      t('page.errors.instagram')
    ).label('Instagram'),
  linkedin: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^https?:\/\/(www\.)?linkedin\.com\/.+$/i,
      t('page.errors.linkedin')
    ).label('Linkedin'),
  youtube: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^https?:\/\/(www\.)?youtube\.com\/.+$/i,
      t('page.errors.youtube')
    ).label('Youtube'),
  twitter: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^https?:\/\/(www\.)?youtube\.com\/.+$/i,
      t('page.errors.twitter')
    ).label('Twitter'),
  twitch: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^https?:\/\/(www\.)?twitch\.com\/.+$/i,
      t('page.errors.twitch')
    ).label('Twitch'),
  amazon: yup
    .string()
    .nullable()
    .notRequired()
    .matches(
      /^https?:\/\/(www\.)?amazon\.com\/.+$/i,
      t('page.errors.amazon')
    ).label('Amazon'),
  customPath: yup.string().min(4).matches(/^[^#%^*\[\]{};':"\\|,.<>/?]*$/).label('customPath')
});

const { defineField, handleSubmit, resetForm, errors } = useForm({
  validationSchema: schema,
});

const [title] = defineField('title');
const [customPath] = defineField('customPath');
const [description] = defineField('description');
const [facebook] = defineField('facebook');
const [instagram] = defineField('instagram');
const [linkedin] = defineField('linkedin');
const [youtube] = defineField('youtube');
const [twitter] = defineField('twitter');
const [twitch] = defineField('twitch');
const [amazon] = defineField('amazon');

const toast = useToast();

const props = defineProps({
  visible: {
    type: Boolean,
    required: true
  },
  page: {
    type: String,
    default: ''
  }
});
const emit = defineEmits(['update:visible']);

const localVisible = ref(props.visible);

const store = useMainStore();
const router = useRouter();

const user = ref();

const initialState = {
  pageLoading: false,
  domainsLoading: false,
  pageToggle: true,
  pageLogoToggle: false,
  pageLinksToggle: false,
  facebook: null,
  instagram: null,
  linkedin: null,
  youtube: null,
  amazon: null,
  customURLTitle: '',
  customURLDestination: '',
  customURLs: [],
  title: '',
  customPath: '',
  customPathDisabled: false,
  customPathInvalid: false,
  customPathStatus: '',
  uuid: '',
  domain: '00000000-0000-0000-0000-000000000000',
  domainsList: [],
  domainDisabled: false,
  description: '',
  PageFileUploadURL: DRUPAL_API_ROUTE.IMAGE_UPLOAD
};

const formData = reactive({ ...initialState });

const isAllowed = computed(() => user.value.plan.hplinks > user.value.plan.pages_usage);

const crsf = ref();

const home = ref({
  icon: 'pi pi-home'
});
const items = ref([
  {
    label: 'Page'
  }
]);

const fetchDomains = async () => {
  formData.domainsLoading = true;
  var currentWorkspace = user.value.current_workspace;
  try {
    var response = await callAPI(DRUPAL_API_ROUTE.WORKSPACES_DOMAINS_LIST, { workspace: currentWorkspace, type: 'shortlinks' });
    formData.domainsList = response.data;

    var workspaceData = JSON.parse(localStorage.getItem('workspaces'));
    var currentWorkspaceData = workspaceData.find(item => item.id === currentWorkspace);
    if (currentWorkspaceData.default_domain) formData.domain = currentWorkspaceData.default_domain;

    formData.domainsLoading = false;
  } catch (err) {
    console.log(err);
  }
};

async function customPathInput() {
  formData.customPath = customPath.value;

  if (formData.customPath.length == 0) {
    formData.customPathInvalid = true;
    return formData.customPathStatus = t('page.custom_path.errors.fill');
  }

  formData.customPathInvalid = false;
  formData.customPathStatus = t('page.custom_path.validation');

  if (formData.customPath.length < 4) return formData.customPathStatus = t('page.custom_path.errors.short');
  formData.customPathInputLoading = true;

  try {
    var response = await callAPI(DRUPAL_API_ROUTE.BULK_TEST, {}, { 'paths': [formData.customPath] });
    if (response.data.code == 404) {
      if (formData.customPath.match(/[^\u0000-\u007F]/g)) {
        formData.customPathStatus = t('page.custom_path.available_nonascii');
      } else {
        formData.customPathStatus = t('page.custom_path.available');
      }
    }
    if (response.data.code == 400) {
      formData.customPathInvalid = true;
      formData.customPathStatus = t('page.custom_path.general.path');
    }
  } catch (err) {
    formData.customPathStatus = t('page.custom_path.errors.general');
  } finally {
    formData.customPathInputLoading = false;
  }
}

const onSubmit = handleSubmit((values) => {
  formData.title = values.title;
  formData.description = values.description;
  formData.facebook = values.facebook;
  formData.instagram = values.instagram;
  formData.linkedin = values.linkedin;
  formData.youtube = values.youtube;
  formData.twitter = values.twitter;
  formData.twitch = values.twitch;
  formData.amazon = values.amazon;

  var update = (formData.uuid) ? true : false;
  generatePage(update);
});

const generatePage = async (update = false) => {
  if (formData.generateLoading) return;

  formData.generateLoading = true;

  var data = {
    "destination": 'https://lc.cx/hplinks',
    "domain": formData.domain,
    "custom_path": formData.customPath,
    "type": 2,
    "extras": {
      "hplinks": {
        "title": formData.title,
        "description": formData.description,
        "logo": formData.pageLogoSRC,
        "links": {
          "facebook": formData.facebook,
          "instagram": formData.instagram,
          "linkedin": formData.linkedin,
          "youtube": formData.youtube,
          "twitter": formData.twitter,
          "twitch": formData.twitch,
          "aws_wishlist": formData.amazon,
          "customs": formData.customURLs
        }
      }
    }
  };

  data.journal = user.value.permissions.save_log;

  if (!update) {
    try {
      var response = await callAPI(DRUPAL_API_ROUTE.SHORTEN, {}, data);
      var page = response.data;
    } catch (err) {
      console.log(err);
    } finally {
      //await generateQRCode(page, false);
      formData.generateLoading = false;
      localVisible.value = false;
      store.setMessage(page);
      resetFormData();
      toast.add({ severity: 'success', summary: 'Page', detail: t('page.create.success'), life: 5000 });
      router.push({
        path: '/pages',
        force: true
      });
    }
  } else {
    try {
      var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK_UPDATE, { uuid: formData.uuid }, data);
      var page = response.data;
    } catch (err) {
      console.log(err);
    } finally {
      formData.generateLoading = false;
      localVisible.value = false;
      store.setMessage(page);
      resetFormData();
      toast.add({ severity: 'info', summary: 'Page', detail: t('page.update.success'), life: 5000 });
      router.push({
        path: '/pages',
        force: true
      });
    }
  }
};

async function fetchPage(uuid) {
  formData.pageLoading = true;

  var response = await callAPI(DRUPAL_API_ROUTE.SHORTLINK, { 'uuid': uuid });
  var page = response.data;

  formData.uuid = page.id;
  title.value = page.extras.hplinks.title;
  formData.customPath = page.path;
  formData.customPathDisabled = true;
  description.value = page.extras.hplinks.description;
  formData.domain = page.domain.id;
  formData.domainDisabled = true;
  formData.pageLogoSRC = page.extras.hplinks.logo;
  if (formData.pageLogoSRC) formData.pageLogoToggle = true;

  formData.pageLinksToggle = true;
  facebook.value = page.extras.hplinks.links.facebook;
  instagram.value = page.extras.hplinks.links.instagram;
  linkedin.value = page.extras.hplinks.links.linkedin;
  twitter.value = page.extras.hplinks.links.twitter;
  youtube.value = page.extras.hplinks.links.youtube;
  amazon.value = page.extras.hplinks.links.aws_wishlist;

  formData.customURLs = page.extras.hplinks.links.customs;
}

async function onBeforeSend(request) {
  request.xhr.setRequestHeader('X-CSRF-Token', crsf.value);
}

const onUpload = async (event) => {
  var response = JSON.parse(event.xhr.response);

  if (response.base64) {
    formData.pageLogo = response.base64;
    formData.pageLogoSRC = 'data:' + response.mime_type + ';base64,' + response.base64;
  }
};

const clearCallback = async () => {
  formData.pageLogo = false;
  formData.pageLogoSRC = '';
}

const addURL = async () => {
  formData.customURLs.push({
    'title': formData.customURLTitle,
    'url': formData.customURLDestination
  });

  formData.customURLTitle = null;
  formData.customURLDestination = null;
};

const deleteURL = async (index) => {
  formData.customURLs.splice(index, 1);
};

function resetFormData() {
  Object.assign(formData, initialState);
}

async function handleShow() {
  user.value = JSON.parse(localStorage.getItem('user'));

  if (props.page) formData.pageLoading = true;
  await fetchDomains();

  if (props.page) {
    await fetchPage(props.page);
    formData.pageLoading = false;
  }

  var response = await callAPI(DRUPAL_API_ROUTE.CRSF_TOKEN);
  crsf.value = response.data;
}

function transformText(text) {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[`#%^*=\[\]{};':"\\|,.<>\/?()]/g, '')
    .replace(/\s+/g, '-')
    .toLowerCase();
}

function handleHide() {
  resetForm();
  resetFormData();
  emit('update:visible', false);
}

onMounted(() => {
  user.value = JSON.parse(localStorage.getItem('user'));
});

watch(
  () => props.visible,
  (newValue) => {
    localVisible.value = newValue;
  }
)
watch(() => formData.title,
  (newValue) => {
    if (!props.page) formData.customPath = transformText(newValue)
  }
)
</script>


<template>
  <Fluid>
    <Drawer :blockScroll="true" @hide="handleHide" @show="handleShow" v-model:visible="localVisible"
      class="!w-full md:!w-80 lg:!w-[35rem]">
      <template #header>
        <div class="flex items-center gap-2 text-xs">
          <Breadcrumb :home="home" :model="items" class="!p-0">
            <template #item="{ item }">
              <span v-if="item.label" class="text-primary mr-2">{{ item.label }}</span>
              <span :class="item.icon"></span>
            </template>
            <template #separator><i class="pi pi-angle-right"></i></template>
          </Breadcrumb>
        </div>
      </template>
      <div class="flex mx-auto">
        <div class="flex flex-col w-full">
          <form @submit="onSubmit" class="space-y-10">
            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.pageToggle" />
                <span class="text-xl">Page</span><i class="pi pi-file ml-auto"></i>
              </div>
              <div v-if="formData.pageToggle" class="space-y-6">
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-2">
                    <label for="title">{{ t('page.title') }}</label>
                    <Skeleton v-if="formData.pageLoading" height="3rem" class="mb-2">
                    </Skeleton>
                    <InputText v-else id="title" size="large" v-model="title" :class="{ 'p-invalid': errors.title }" />
                    <Message v-if="errors.title" severity="error" size="small" variant="simple">{{
                      errors.title }}
                    </Message>
                  </div>
                  <div class="flex flex-row flex-nowrap">
                    <div class="flex flex-col gap-2">
                      <label for="domain">{{ t('page.domain') }}</label>
                      <Skeleton v-if="formData.pageLoading || formData.domainsLoading" height="3rem" class="mb-2">
                      </Skeleton>
                      <Select v-else v-model="formData.domain" :options="formData.domainsList" optionValue="uuid"
                        optionLabel="domain" :placeholder="t('common.placeholders.select')"
                        :disabled="formData.domainDisabled" />
                    </div>
                    <div class="basis-1/12 flex-col gap-2 text-center">
                      <span class="text-4xl mt-8 hidden sm:block">/</span>
                    </div>
                    <div class="grow flex flex-col gap-2">
                      <label for="custom_path">{{ t('page.custom_path.title') }}</label>
                      <Skeleton v-if="formData.pageLoading" height="3rem" class="mb-2">
                      </Skeleton>
                      <IconField v-else>
                        <InputGroup>
                          <InputText v-model="customPath" @input="customPathInput"
                            :disabled="formData.customPathDisabled" :invalid="formData.customPathInvalid" />
                        </InputGroup>
                      </IconField>
                      <Message v-if="errors.customPath" severity="error" size="small" variant="simple">{{
                        errors.customPath }} </Message>
                      <Message v-if="formData.customPathStatus == t('page.custom_path.available')" severity="success"
                        size="small" variant="simple"><i class="pi pi-check"></i> {{
                          formData.customPathStatus }}
                      </Message>
                      <Message v-if="formData.customPathStatus == t('page.custom_path.available_nonascii')"
                        severity="warn" size="small" variant="simple"><i class="pi pi-exclamation-triangle"></i> {{
                          formData.customPathStatus }}
                      </Message>
                    </div>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label for="title">{{ t('page.description') }}</label>
                    <Skeleton v-if="formData.pageLoading" height="3rem" class="mb-2">
                    </Skeleton>
                    <Textarea v-model="description" rows="5" cols="30" :class="{ 'p-invalid': errors.description }" />
                    <Message v-if="errors.description" severity="error" size="small" variant="simple">{{
                      errors.description }}
                    </Message>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.pageLogoToggle" />
                <span class="text-xl">{{ t('page.logo') }}</span><i class="pi pi-image ml-auto"></i>
              </div>
              <div v-if="formData.pageLogoToggle" class="space-y-6">
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col items-center gap-4">
                    <div v-if="!formData.pageLogoSRC">
                      <FileUpload :withCredentials="true" @before-send="onBeforeSend" mode="basic" name="file"
                        :url="formData.PageFileUploadURL" accept="image/*" :maxFileSize="1000000" @upload="onUpload"
                        :auto="true" :chooseLabel="t('common.labels.browse')" />
                    </div>
                    <div v-else class="flex space-x-4">
                      <img class="max-w-32" :src="formData.pageLogoSRC" />
                      <Button @click="clearCallback()" icon="pi pi-times" rounded outlined severity="danger"></Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex flex-col gap-2 space-y-4">
              <div class="flex items-center space-x-2">
                <ToggleSwitch v-model="formData.pageLinksToggle" />
                <span class="text-xl">{{ t('page.links') }}</span><i class="pi pi-link ml-auto"></i>
              </div>
              <div v-if="formData.pageLinksToggle" class="space-y-6">
                <div class="flex flex-col gap-4">
                  <div class="flex flex-col gap-2">
                    <label for="custom_path">Facebook</label>
                    <InputGroup>
                      <InputGroupAddon>
                        <i class="pi pi-facebook"></i>
                      </InputGroupAddon>
                      <InputText v-model="facebook" placeholder="https://" :class="{ 'p-invalid': errors.facebook }" />
                    </InputGroup>
                    <Message v-if="errors.facebook" severity="error" size="small" variant="simple">{{
                      errors.facebook }}
                    </Message>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label for="custom_path">Instagram</label>
                    <InputGroup>
                      <InputGroupAddon>
                        <i class="pi pi-instagram"></i>
                      </InputGroupAddon>
                      <InputText v-model="instagram" placeholder="https://"
                        :class="{ 'p-invalid': errors.instagram }" />
                    </InputGroup>
                    <Message v-if="errors.instagram" severity="error" size="small" variant="simple">{{
                      errors.instagram }}
                    </Message>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label for="custom_path">X (ex-Twitter)</label>
                    <InputGroup>
                      <InputGroupAddon>
                        <i class="pi pi-twitter"></i>
                      </InputGroupAddon>
                      <InputText v-model="twitter" placeholder="https://" :class="{
                        'p-invalid': errors.twitter
                      }" />
                    </InputGroup>
                    <Message v-if="errors.twitter" severity="error" size="small" variant="simple">{{
                      errors.twitter }}
                    </Message>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label for="custom_path">Linkedin</label>
                    <InputGroup>
                      <InputGroupAddon>
                        <i class="pi pi-linkedin"></i>
                      </InputGroupAddon>
                      <InputText v-model="linkedin" placeholder="https://" :class="{ 'p-invalid': errors.linkedin }" />
                    </InputGroup>
                    <Message v-if="errors.linkedin" severity="error" size="small" variant="simple">{{
                      errors.linkedin }}
                    </Message>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label for="custom_path">Youtube</label>
                    <InputGroup>
                      <InputGroupAddon>
                        <i class="pi pi-youtube"></i>
                      </InputGroupAddon>
                      <InputText v-model="youtube" placeholder="https://" :class="{ 'p-invalid': errors.youtube }" />
                    </InputGroup>
                    <Message v-if="errors.youtube" severity="error" size="small" variant="simple">{{
                      errors.youtube }}
                    </Message>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label for="custom_path">Twitch</label>
                    <InputGroup>
                      <InputGroupAddon>
                        <i class="pi pi-twitch"></i>
                      </InputGroupAddon>
                      <InputText v-model="twitch" placeholder="https://" :class="{ 'p-invalid': errors.twitch }" />
                    </InputGroup>
                    <Message v-if="errors.twitch" severity="error" size="small" variant="simple">{{
                      errors.twitch }}
                    </Message>
                  </div>
                  <div class="flex flex-col gap-2">
                    <label for="custom_path">Amazon Wish List</label>
                    <InputGroup>
                      <InputGroupAddon>
                        <i class="pi pi-amazon"></i>
                      </InputGroupAddon>
                      <InputText v-model="amazon" placeholder="https://" :class="{ 'p-invalid': errors.amazon }" />
                    </InputGroup>
                    <Message v-if="errors.amazon" severity="error" size="small" variant="simple">{{
                      errors.amazon }}
                    </Message>
                  </div>


                  <div class="flex flex-row space-x-4 mt-5">
                    <div class="flex flex-row space-x-10">
                      <div class="flex flex-col gap-2 w-1/2">
                        <label for="country">{{ t('page.custom.title') }}</label>
                        <InputText v-model="formData.customURLTitle" />
                      </div>
                      <div class="flex flex-col gap-2 w-1/2">
                        <label for="destination">{{ t('page.custom.link') }}</label>
                        <InputText v-model="formData.customURLDestination" placeholder="https://" />
                      </div>
                      <div class="flex gap-2 mt-auto justify-end">
                        <Button @click="addURL(formData.customURLTitle, formData.customURLDestination)"
                          icon="pi pi-plus-circle" v-tooltip.bottom="'Ajouter'" severity="primary" />
                      </div>
                    </div>
                  </div>


                  <div v-for="(customURLItem, index) in formData.customURLs" :key="index"
                    class="flex flex-row space-x-10 mt-10">
                    <div class="flex flex-col gap-2 w-1/2">
                      <InputText v-model="customURLItem.title" disabled />
                    </div>
                    <div class="flex flex-col gap-2 w-1/2">
                      <InputText v-model="customURLItem.url" disabled />
                    </div>
                    <div class="flex gap-2 mt-auto justify-end">
                      <Button @click="deleteURL(index)" v-tooltip.bottom="t('common.labels.delete')" severity="danger"
                        icon="pi pi-trash" size="small" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="flex flex-col mt-1" v-if="!isAllowed && !formData.uuid">
              <Message icon="pi pi-info-circle" severity="info">{{ t('page.notallowed') }}
              </Message>
            </div>
          </form>
        </div>
      </div>
      <template #footer>
        <div v-if="!formData.pageLoading" class="flex items-center gap-2">
          <Button @click="handleHide" :label="t('common.labels.cancel')" class="flex-auto" outlined />
          <Button v-if="!formData.uuid" type="submit" @click="onSubmit" :disabled="!isAllowed"
            :loading="formData.generateLoading" :label="t('common.labels.create')" />
          <Button v-else type="submit" @click="onSubmit" :loading="formData.generateLoading"
            :label="t('common.labels.update')" />
        </div>
      </template>
    </Drawer>
  </Fluid>
</template>
